// @ts-ignore
/*---------------React & Libraries-----------------*/
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { observer } from "mobx-react-lite";

/*---------------Components-----------------------*/
import SearchBox from "./components/SearchBox";
import ProductCard from "./components/ProductCard";
import FloatingCartButton from "./components/FloatingCartButton";
import DeliveryTypeSwitch from "./components/DeliveryTypeSwitch";

/*---------------Material UI Components-----------*/
import { Typography, Box, CircularProgress, Button } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";

/*---------------Stores---------------------------*/
import { store, useStore } from "../../app/stores/store";

/*---------------Types---------------------------*/
import { AddToCartReqParams } from "../../app/models/AddedCartItems";
import BreadcrumbsNav from "../../app/shared/common/Miscellaneous/BreadCrumbNav";
import { CreateCartBreadCrumbItem } from "../../app/data/breadCrumbData";
import CategoryFilter from "./components/CategoryFilter";
import SubcategoryFilter from "./components/SubcategoryFilter";

const Index: React.FC = () => {
  const navigate = useNavigate();
  const { outletId, customerTypeId, customerId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { commonStore, cartStore, productStore, toasterStore, quickDeliveryStore } = useStore();
  const { customer } = commonStore;
  const { cartItems, getCartItems, addTocart, uploadExcel, loadingExcelUpload } = cartStore;
  const { products, searchProducts, deliveryType, changeDeliveryType } =
    productStore;
  const { fetchCustomerDetail, loadingCustomerDetail: loadingCustomer } =
    commonStore;
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [selectedSubCategories, setSelectedSubCategories] = useState<string[]>(
    []
  );
  const warehouseId = store.commonStore.warehouseId;  

  const handleDeliveryType = async (val: number) => {
    try {
      // Always fetch quick delivery configuration on every toggle
      await quickDeliveryStore.getQuickDeliveryConfiguration({
        delivery_type: val,
        warehouseId: Number(warehouseId)
      });

      // Check if switching to quick delivery (delivery type 2)
      if (val === 2) {
        // Validate if quick delivery is available
        if (!quickDeliveryStore.validateQuickDeliveryAvailability()) {
          return; // Stop if not available, error toast already shown
        }
      }
    } catch (error) {
      toasterStore.error("Failed to check delivery availability");
      return;
    }
    
    // Proceed with delivery type change
    changeDeliveryType(val);
  };

  const handleCategoryChange = (categoryIds: number[]) => {
    setSelectedCategories(categoryIds);
  };

  const handleSubCategoryChange = (subCategoryNames: string[]) => {
    setSelectedSubCategories(subCategoryNames);
  };

  const handleApplyFilter = () => {
    // Set filters in ProductStore
    productStore.setFilters(selectedCategories, selectedSubCategories);

    // Clear search query when applying filters
    setSearchQuery("");

    // Trigger product search with filters (empty search query)
    productStore.searchProducts("");
  };

  const handleClearFilters = (clearType: "all" | "subcategories" = "all") => {
    if (clearType === "all") {
      // Clear everything
      setSelectedCategories([]);
      setSelectedSubCategories([]);
      productStore.clearFilters();
      productStore.products = null;
    } else if (clearType === "subcategories") {
      // Only clear subcategories, keep categories and re-apply filter
      setSelectedSubCategories([]);
      productStore.setFilters(selectedCategories, []);
      productStore.searchProducts("");
    }
  };

  useEffect(() => {
    const urlDeliveryType = Number(searchParams.get("deliveryType") || "1");
    // Only set delivery type from URL if there are available delivery types
    if (commonStore.actualAvailableDeliveryTypes?.length > 0 && urlDeliveryType !== deliveryType) {
      changeDeliveryType(urlDeliveryType);
    }
    //@ts-ignore
  }, []);

  useEffect(() => {
    // Only add deliveryType to URL if there are available delivery types
    if (commonStore.actualAvailableDeliveryTypes?.length > 0) {
      const currentParam = searchParams.get("deliveryType");
      if (currentParam !== String(deliveryType)) {
        searchParams.set("deliveryType", String(deliveryType));
        setSearchParams(searchParams, { replace: true });
      }
    } else {
      // Remove deliveryType from URL if no delivery types available
      if (searchParams.has("deliveryType")) {
        searchParams.delete("deliveryType");
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [deliveryType, searchParams, setSearchParams, commonStore.actualAvailableDeliveryTypes]);

  const outletInfo = {
    outletId,
    warehouseId: Number(warehouseId),
    customerTypeId: customer?.customerTypeId || customerTypeId,
  };

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const handleAutocompleteSearch = useCallback(
    (value: string) => {
      // Call autocomplete API immediately (no debounce)
      productStore.autocomplete(value);
    },
    [productStore]
  );

  const handleSuggestionClick = useCallback(
    (suggestion: any) => {
      // When user clicks a suggestion, filter by subcategory name using fieldname/fieldvalue params
      const subcategoryName: string = suggestion.subcategory;
      setSearchQuery(subcategoryName);
      // Set subcategory filter so searchProducts sends fieldname=subcategory_name&fieldvalue=...
      productStore.setFilters([], [subcategoryName]);
      // Trigger search with empty text query — subcategory filter handles it
      productStore.searchProducts("");
    },
    [productStore]
  );

  const goToCart = () => {
    // Prevent navigation if no delivery types available
    if (!commonStore.actualAvailableDeliveryTypes || commonStore.actualAvailableDeliveryTypes.length === 0) {
      toasterStore.error("No delivery options available for this outlet");
      return;
    }

    navigate(
      `/cartDetail?outletId=${outletInfo.outletId}&warehouseId=${warehouseId}&customerTypeId=${outletInfo.customerTypeId}&deliveryType=${deliveryType}`
    );
    localStorage.setItem(
      "cart_context",
      JSON.stringify({
        outletId: outletInfo.outletId,
        warehouseId: Number(warehouseId),
        customerTypeId: outletInfo.customerTypeId,
      })
    );
  };

  useEffect(() => {
    // Clear previous products when navigating to this page or when outlet changes
    productStore.products = null;
    productStore.clearFilters();
    setSearchQuery("");
    
    // Set outlet's customerTypeId as fallback (used only if admin doesn't have customerTypeId from verify-admin-otp)
    if (customerTypeId) {
      productStore.setOutletCustomerTypeId(Number(customerTypeId));
    }
    
    // Always fetch customer detail on mount or when customerId changes
    if (customerId) {
      fetchCustomerDetail(Number(customerId));
    }
  }, [customerId, customerTypeId, fetchCustomerDetail, productStore]);

  // Auto-switch delivery type if current type is not available
  useEffect(() => {
    if (!loadingCustomer && commonStore.actualAvailableDeliveryTypes?.length > 0) {
      if (!commonStore.actualAvailableDeliveryTypes.includes(deliveryType)) {
        // Switch to the first available delivery type
        const firstAvailable = commonStore.actualAvailableDeliveryTypes[0];
        changeDeliveryType(firstAvailable);
      }
    }
  }, [loadingCustomer, commonStore.actualAvailableDeliveryTypes, deliveryType, changeDeliveryType]);

  useEffect(() => {
    if (loadingCustomer) return;

    if (warehouseId === undefined || warehouseId === null) return;

    if (!warehouseId) {
      toasterStore.error(
        "This area is not serviceable. Please update your delivery address."
      );
      return;
    }

    // @ts-ignore
    getCartItems({
      outletId: Number(outletInfo.outletId),
      businessTypeId: Number(outletInfo.customerTypeId),
      warehouseId: Number(warehouseId),
      order_type: deliveryType,
    });
    if (searchQuery.trim()) {
      // Clear filters when searching
      productStore.clearFilters();
      productStore.searchProducts(searchQuery);
    }
  }, [
    customer,
    loadingCustomer,
    warehouseId,
    searchQuery,
    deliveryType,
    getCartItems,
    searchProducts,
  ]);

  // Load categories when component mounts
  useEffect(() => {
    if (loadingCustomer) return;
    if (warehouseId === undefined || warehouseId === null) return;
    if (!warehouseId) return;

    // Load categories only if not already loaded
    if (productStore.categories.length === 0) {
      productStore.loadCategories();
    }
  }, [loadingCustomer, warehouseId, productStore, outletId]);

  const addToCart = async ({
    productId,
    quantity,
  }: {
    productId: number;
    quantity: number;
  }) => {
    try {
      if (!warehouseId) {
        toasterStore.error(
          "This area is not serviceable. Please update your delivery address."
        );
        return;
      }
      const payload: AddToCartReqParams = {
        productId,
        quantity,
        order_delivery_type: deliveryType,
        clear_cart: false,
        businessTypeId: Number(outletInfo.customerTypeId),
        outletId: Number(outletInfo.outletId),
        warehouseId: Number(warehouseId),
      };
      await addTocart(payload);
    } catch (err: unknown) {
      if (err instanceof Error) {
        toasterStore.error(err.message);
      } else {
        toasterStore.error("Something went wrong");
      }
    }
  };

  if (loadingCustomer) {
    return <Typography sx={{ p: 2 }}>Loading customer info...</Typography>;
  }

  const handleExcelUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && customer) {
      try {
        if (!warehouseId) {
          toasterStore.error("Warehouse not configured");
          return;
        }
        const result = await uploadExcel(
          file,
          Number(warehouseId),
          Number(customer.customerTypeId),
          Number(customer.id)
        );
        if (result?.status_code === 1) {
          toasterStore.success(result?.message || "Excel uploaded successfully");
          // Navigate to cart detail page after successful upload
          navigate(
            `/cartDetail?outletId=${outletInfo.outletId}&warehouseId=${warehouseId}&customerTypeId=${outletInfo.customerTypeId}&deliveryType=${deliveryType}`
          );
        } else {
          toasterStore.error(result?.message || "Failed to upload excel");
        }
      } catch (error: any) {
        toasterStore.error(error?.response?.data?.message || "Failed to upload excel");
      }
    }
    // Reset input value to allow selecting the same file again
    event.target.value = "";
  };

  return (
    <>
      {/* Top header: title (optional) + switch on the right */}
      <Box
        sx={{
          px: { xs: 0, md: 2 },
          pt: { xs: 0, md: 2 },
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: { xs: 1.5, sm: 0 },
        }}
      >
        <BreadcrumbsNav
          items={CreateCartBreadCrumbItem(
            customerId!,
            customer?.name // optional — falls back to "Customer Detail"
          )}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            gap: { xs: 1.5, sm: 2 },
          }}
        >
          <DeliveryTypeSwitch
            deliveryType={deliveryType}
            handleDeliveryType={handleDeliveryType}
            availableDeliveryTypes={commonStore.actualAvailableDeliveryTypes}
          />
          {commonStore.customerTypeId !== null && (
            <Button
              variant="outlined"
              component="label"
              startIcon={loadingExcelUpload ? <CircularProgress size={20} /> : <UploadFileIcon />}
              sx={{ textTransform: "none", fontWeight: 600 }}
              disabled={loadingExcelUpload}
            >
              {loadingExcelUpload ? "Uploading..." : "Upload Excel"}
              <input
                type="file"
                hidden
                accept=".xlsx,.xls"
                onChange={handleExcelUpload}
              />
            </Button>
          )}
        </Box>
      </Box>

      {/* Full-width search below */}
      <Box sx={{ px: { xs: 0, md: 2 }, mt: 1 }}>
        <SearchBox
          placeholder="Search Products"
          onSearchChange={handleSearchChange}
          onAutocompleteSearch={handleAutocompleteSearch}
          autocompleteSuggestions={productStore.autocompleteSuggestions}
          loadingAutocomplete={productStore.loadingAutocomplete}
          onSuggestionClick={handleSuggestionClick}
          debounceMs={800}
        />
      </Box>
      {searchQuery && (
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{
            px: { xs: 0, md: 2 },
            mt: 0,
            mb: { xs: 1, md: 0 },
          }}
        >
          Showing results for "<strong>{searchQuery}</strong>"
        </Typography>
      )}

      {/* Only show category and subcategory filters if user does not have customerTypeId */}
      {!commonStore.customerTypeId && (
        <Box
          sx={{ p: { xs: 0, md: 2 }, display: "flex", gap: 1, flexWrap: "wrap" }}
        >
          <CategoryFilter
            selectedCategories={selectedCategories}
            onCategoryChange={handleCategoryChange}
            onApplyFilter={handleApplyFilter}
            onClearFilters={handleClearFilters}
          />
          <SubcategoryFilter
            selectedCategories={selectedCategories}
            selectedSubCategories={selectedSubCategories}
            onSubCategoryChange={handleSubCategoryChange}
            onApplyFilter={handleApplyFilter}
            onClearFilters={handleClearFilters}
          />
        </Box>
      )}

      {productStore.loadingProducts ? (
        // show loader while searching
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
            gap: 2,
          }}
        >
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary">
            Loading products...
          </Typography>
        </Box>
      ) : (
        // Render products if we have search results OR filters applied
        (searchQuery ||
          productStore.selectedCategoryIds.length > 0 ||
          productStore.selectedSubCategoryNames.length > 0) &&
        products && (
          <Box sx={{ p: { xs: 0, md: 2 } }}>
            {products?.data?.products?.map((p) => {
              const cartQuantities: Record<number, number> = {};
              cartItems?.data.cart_items.forEach((item: any) => {
                cartQuantities[item.productId] = item.quantity;
              });

              const mappedProduct = {
                id: p.id,
                title: p.name,
                unit: p.unit,
                price: p.base_price,
                image:
                  p.product_images?.find(
                    (img: any) => img.imageType === "thumbnail"
                  )?.path || "",
                product_varients: p.product_varients.map((v: any) => ({
                  ...v,
                  quantity_in_cart: cartQuantities[p.id] || 0,
                  total_stock: p.total_stock,
                })),
                max_quantity_standard: p.max_quantity_standard,
                max_quantity_quick: p.max_quantity_quick,
                total_stock: p.total_stock,
                base_denomination: p.base_denomination,
                base_denomination_uom: p.base_denomination_uom,
              };

              return (
                <ProductCard
                  key={mappedProduct.id}
                  product={mappedProduct}
                  deliveryType={deliveryType}
                  onAddToCart={addToCart}
                />
              );
            })}
          </Box>
        )
      )}

      {cartItems && (
        <FloatingCartButton
          cartCount={cartItems?.data.cart_items.length}
          onClick={goToCart}
        />
      )}
    </>
  );
};

export default observer(Index);
